import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { base44 } from '@/api/base44Client';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Brain, Send, Minimize2, Maximize2, X, Sparkles, User, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const PHASE_QUICK_ACTIONS = {
  1: [
    { label: 'Summarize queue health', message: 'Give me a quick summary of the current issue queue health and what I should focus on today.' },
    { label: 'Critical items?', message: 'Are there any critical items I should address first in this session?' },
    { label: 'Last session carryover', message: 'What carried over from the last session that we need to follow up on?' }
  ],
  2: [
    { label: 'Help classify this issue', message: 'Help me apply the 6 Signal Detection Tests to the current issue. Walk me through each test.' },
    { label: 'Is this noise or signal?', message: 'Based on the data available, what is your recommendation — noise or signal? Give me your reasoning.' },
    { label: 'Similar past issues?', message: 'Are there similar issues in the archive that might inform how we classify this one?' }
  ],
  3: [
    { label: 'Balloon or rock?', message: 'Help me assess density for the current issue. Walk me through the 6 Density Detection Tests.' },
    { label: 'What makes this structural?', message: 'What evidence suggests this issue is structural (rock) versus surface-level (balloon)?' },
    { label: 'Capability gap check', message: 'Does resolving this issue require a new capability, or can it be handled with existing resources?' }
  ],
  4: [
    { label: 'Suggest resolution path', message: 'What resolution approach do you recommend for the current issue? Consider the 3-track model for rocks.' },
    { label: 'Who should own this?', message: 'Based on the issue details and officer profiles, who would be the best resolution owner?' },
    { label: 'Policy or capability needed?', message: 'Does this issue need a new bridging policy, a capability development track, or both?' }
  ],
  5: [
    { label: 'Session patterns', message: 'What patterns do you see across the issues we processed today? Any systemic themes?' },
    { label: 'Draft upward report', message: 'Help me draft the key findings for the upward report. Focus on strategic implications, not operational details.' },
    { label: 'Next session priorities', message: 'What should we prioritize in the next session based on what we covered today?' }
  ]
};

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: "I'm the **Council Advisor** — your strategic analyst for this session. I have full access to all issue data, missions, objectives, policies, and capabilities.\n\nAsk me anything about issues, resolution tracks, patterns, or what to prioritize."
};

export default function CouncilAdvisorPanel({ sessionId, currentPhase, currentIssueId }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastPhase, setLastPhase] = useState(null);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, minimized]);

  // Phase transition auto-message
  useEffect(() => {
    if (currentPhase && currentPhase !== lastPhase && lastPhase !== null) {
      const phaseNames = {
        1: 'State of the Field',
        2: 'Signal Detection (Triage)',
        3: 'Density Detection',
        4: 'Resolution Routing',
        5: 'Session Closing'
      };
      setMessages(prev => [...prev, {
        role: 'system',
        content: `— Phase ${currentPhase}: ${phaseNames[currentPhase]} —`
      }]);
    }
    setLastPhase(currentPhase);
  }, [currentPhase]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const history = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }));

    const response = await base44.functions.invoke('councilAdvisorChat', {
      message: text.trim(),
      conversation_history: history,
      session_id: sessionId,
      current_phase: currentPhase,
      current_issue_id: currentIssueId
    });

    const data = response.data || response;
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: data.response || 'No response generated.',
      suggested_actions: data.suggested_actions || []
    }]);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleRestart = () => {
    setMessages([INITIAL_MESSAGE]);
    setInput('');
  };

  const quickActions = PHASE_QUICK_ACTIONS[currentPhase] || PHASE_QUICK_ACTIONS[1];

  // Floating trigger button
  if (!open) {
    return createPortal(
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/30 flex items-center justify-center transition-all hover:scale-110"
        title="Open Council Advisor"
      >
        <Brain className="w-6 h-6" />
        {messages.length > 1 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full text-[10px] font-bold flex items-center justify-center">
            {messages.filter(m => m.role === 'assistant').length}
          </span>
        )}
      </button>,
      document.body
    );
  }

  // Minimized bar
  if (minimized) {
    return createPortal(
      <div className="fixed bottom-6 right-6 z-[9999] w-[300px] bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl overflow-hidden">
        <div data-testid="hc-counciladvisorpanel-clickable-div" className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 cursor-pointer" onClick={() => setMinimized(false)}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Brain className="w-3 h-3 text-purple-400" />
            </div>
            <span className="font-semibold text-sm text-white">Council Advisor</span>
            {messages.length > 1 && (
              <span className="text-[10px] text-slate-500">({messages.length - 1} messages)</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setMinimized(false); }} className="text-slate-400 hover:text-slate-200 h-6 w-6">
              <Maximize2 className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setOpen(false); setMinimized(false); }} className="text-slate-400 hover:text-slate-200 h-6 w-6">
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Full chat panel
  return createPortal(
    <div className="fixed bottom-6 right-6 z-[9999] w-[420px] max-w-[calc(100vw-2rem)] h-[520px] bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl shadow-purple-500/10 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/50 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Brain className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <span className="font-semibold text-sm text-white">Council Advisor</span>
            <p className="text-[10px] text-slate-400">
              Strategic Analyst{currentPhase ? ` · Phase ${currentPhase}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={handleRestart} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Restart conversation">
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMinimized(true)} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Minimize">
            <Minimize2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => { setOpen(false); setMinimized(false); }} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3" ref={scrollRef}>
        <div className="space-y-3">
          {messages.map((msg, idx) => {
            if (msg.role === 'system') {
              return (
                <div key={idx} className="text-center">
                  <span className="text-[10px] text-purple-500 bg-purple-500/10 px-2 py-0.5 rounded-full">{msg.content}</span>
                </div>
              );
            }

            return (
              <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <Brain className="w-3 h-3 text-purple-400" />
                  </div>
                )}
                <div className={`max-w-[85%] min-w-0 px-3 py-2 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-800 text-slate-200'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none break-words overflow-wrap-anywhere [&_p]:m-0 [&_p]:break-words [&_ul]:my-1 [&_li]:my-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-all [&_code]:whitespace-pre-wrap">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  )}
                  {/* Suggested Actions */}
                  {msg.suggested_actions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-700">
                      {msg.suggested_actions.map((action, aidx) => (
                        <Badge
                          key={aidx}
                          className="bg-purple-500/20 text-purple-300 text-[10px] cursor-pointer hover:bg-purple-500/30"
                          onClick={() => sendMessage(action)}
                        >
                          {action}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-3 h-3 text-purple-300" />
                  </div>
                )}
              </div>
            );
          })}

          {loading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Brain className="w-3 h-3 text-purple-400" />
              </div>
              <div className="bg-slate-800 px-3 py-2 rounded-lg">
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Quick Actions */}
      {messages.length < 3 && (
        <div className="px-3 py-2 border-t border-slate-800 bg-slate-900/80 shrink-0">
          <p className="text-[10px] text-slate-500 mb-1.5 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Quick actions
          </p>
          <div className="flex flex-wrap gap-1">
            {quickActions.map((action, idx) => (
              <Button
                key={idx}
                size="sm"
                variant="outline"
                onClick={() => sendMessage(action.message)}
                disabled={loading}
                className="h-6 text-[10px] border-purple-500/20 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200"
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-slate-700 shrink-0">
        <div className="flex gap-2">
          <Textarea data-testid="hc-counciladvisorpanel-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask the advisor..."
            className="bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-500 min-h-[40px] max-h-[80px] resize-none text-sm flex-1"
            disabled={loading}
          />
          <Button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 self-end"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Send className="w-4 h-4" />
            }
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}